import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useModalContext } from "../state/ModalContext/ModalContext";
import { StatusModal } from "../../UI/StatusModal/StatusModal";

export interface StatusModalBody {
  data: {
    image?: ReactNode;
    title: string;
    description?: string;
    cta?: string;
    cancelCta?: string;
  };
  status: "success" | "error" | "info";
  dismissCallback?: () => void;
  cancelCallback?: () => void;
  callback?: () => void;
  ignoreHide?: boolean;
  showDismiss?: boolean;
  dismissAfter?: number;
}

export const useStatusModal = (prefix?: string) => {
  const [body, setBody] = useState<StatusModalBody | null>(null);
  const { isVisible: isVisibleError, hide: hideError, show: showError } = useModalContext(`${prefix ?? ""}-error-modal`);
  const { isVisible: isVisibleInfo, hide: hideInfo, show: showInfo } = useModalContext(`${prefix ?? ""}-info-modal`);
  const { isVisible: isVisibleSuccess, hide: hideSuccess, show: showSuccess } = useModalContext(`${prefix ?? ""}-success-modal`);

  const handleCallbacks = useCallback(
    (callback?: () => void) => {
      if (!body?.ignoreHide) {
        body?.status === "info" && hideInfo();
        body?.status === "error" && hideError();
        body?.status === "success" && hideSuccess();
      }
      callback && callback();
    },
    [body?.ignoreHide, body?.status, hideError, hideInfo, hideSuccess]
  );

  const handleCancel = useCallback(() => handleCallbacks(body?.cancelCallback), [body?.cancelCallback, handleCallbacks]);

  const handleDismiss = useCallback(() => handleCallbacks(body?.dismissCallback), [body?.dismissCallback, handleCallbacks]);

  const handleConfirm = useCallback(() => handleCallbacks(body?.callback), [body?.callback, handleCallbacks]);

  const modalComponent = useMemo(
    () =>
      body ? (
        <StatusModal
          data={body.data}
          status={body.status}
          showDismiss={body.showDismiss}
          handleConfirm={handleConfirm}
          handleCancel={body?.cancelCallback ? handleCancel : undefined}
          handleDismiss={body?.dismissCallback ? handleDismiss : () => handleCallbacks()}
        />
      ) : null,
    [body, handleConfirm, handleDismiss, handleCancel, handleCallbacks]
  );

  useEffect(() => {
    if (!!body) {
      if (body.status === "info") {
        showInfo();
      }
      if (body.status === "error") {
        showError();
      }
      if (body.status === "success") {
        showSuccess();
      }
    } else if (!!modalComponent) {
      handleDismiss();
    }
  }, [body, hideError, hideSuccess, showError, showSuccess, handleDismiss, modalComponent, showInfo]);

  useEffect(() => {
    if (!!body?.dismissAfter) {
      setTimeout(() => {
        handleDismiss();
      }, body.dismissAfter);
    }
  }, [body?.dismissAfter, handleDismiss]);

  return {
    Modal: isVisibleError || isVisibleSuccess || isVisibleInfo ? modalComponent : null,
    setBody,
  };
};
